const COUNTRY_ENDPOINT = 'api/v1/country';
const STATE_ENDPOINT = 'api/v1/state';
const CITY_ENDPOINT = 'api/v1/city';

export const API_CONSTANTS = {
  BASE_URL: (() => {
    const url = import.meta.env.NX_PUBLIC_API_BASE_URL as string | undefined;

    if (typeof window !== 'undefined') {
      const host = window.location.hostname.toLowerCase();
      if (host === 'localhost' || host === '127.0.0.1') {
        return 'http://localhost:5000/';
      }
      if (host.includes('fadeoutapp.com')) {
        return 'https://api.fadeoutapp.com/';
      }
      if (host.includes('projectspreview.net')) {
        return 'https://api-fadeout-dev.projectspreview.net/';
      }
    }

    const base = url ?? 'http://localhost:5000/';
    return base.endsWith('/') ? base : `${base}/`;
  })(),
  VALIDATE_EMAIL_ENDPOINT: 'api/v1/panel/validate',
  LOGIN_ENDPOINT: 'api/v1/panel/login',
  FORGOT_PASSWORD_ENDPOINT: 'api/v1/forgot-password',
  LOGOUT_ENDPOINT: 'api/v1/logout',
  RESET_PASSWORD_ENDPOINT: 'api/v1/panel/reset-password',
  VERIFY_OTP_ENDPOINT: 'api/v1/verify-otp',
  REFRESH_TOKEN_ENDPOINT: 'api/v1/panel/refresh',
  MENU_ENDPOINT: 'api/v1/menus',
  IDENTITY_ENDPOINT: 'api/v1/identity',
  PROFILE_UPDATE_ENDPOINT: 'api/v1/admin/profile-update',
  CHANGE_PASSWORD_ENDPOINT: 'api/v1/user/change-password',
  LINK_VERIFICATION_ENDPOINT: 'api/v1/panel/link-verification',
  ONBOARDING_VERIFY_ENDPOINT: 'api/v1/auth/onboarding/verify-email',
  ONBOARDING_COMPLETE_ENDPOINT: 'api/v1/auth/onboarding/verify-email',
  COUNTRIES_ENDPOINT: 'api/v1/country/list',
  COUNTRIES_AUTOCOMPLETE_ENDPOINT: 'api/v1/country/autocomplete?type=Active',
  COUNTRIES_ADD_ENDPOINT: COUNTRY_ENDPOINT,
  COUNTRIES_GET_ENDPOINT: COUNTRY_ENDPOINT,
  COUNTRIES_EDIT_ENDPOINT: COUNTRY_ENDPOINT,
  COUNTRIES_CHANGE_STATUS_ENDPOINT: 'api/v1/country/change-status',
  STATES_ENDPOINT: 'api/v1/state/list',
  STATES_ADD_ENDPOINT: STATE_ENDPOINT,
  STATES_GET_ENDPOINT: STATE_ENDPOINT,
  STATES_EDIT_ENDPOINT: STATE_ENDPOINT,
  STATES_CHANGE_STATUS_ENDPOINT: 'api/v1/state/change-status',
  CITIES_ENDPOINT: 'api/v1/city/list',
  CITIES_ADD_ENDPOINT: CITY_ENDPOINT,
  CITIES_GET_ENDPOINT: CITY_ENDPOINT,
  CITIES_EDIT_ENDPOINT: CITY_ENDPOINT,
  CITIES_CHANGE_STATUS_ENDPOINT: 'api/v1/city/change-status',
  GET_ALL_LANGUAGES_ENDPOINT: '/languages',
  FILE_UPLOAD_URL: 'api/v1/file-upload',
  PERMISSIONS: {
    PERMISSIONS_LIST_URL: 'api/v1/permission/permission-list',
    PERMISSIONS_GROUP_URL: 'api/v1/permission/permission-group',
    ADD_PERMISSION_URL: 'api/v1/permission/add',
  },
  EMAIL: {
    SYSTEM_EMAIL_LIST_URL: 'api/v1/system-email/list',
    ADD_SYSTEM_EMAIL_URL: 'api/v1/system-email',
    GET_SINGLE_EMAIL_URL: (id: string | number) => `/api/v1/system-email/${id}`,
    UPDATE_EMAIL_URL: (id: string | number) => `/api/v1/system-email/${id}`,
  },
  EMAIL_NOTIFICATIONS: {
    LIST_URL: 'api/v1/notification/email',
    DETAIL_URL: (id: string | number) =>
      `api/v1/notification/email/detail/${id}`,
    EDIT_URL: 'api/v1/notification/email/edit',
  },
  SYSTEM_NOTIFICATIONS: {
    LIST_URL: 'api/v1/notification/system',
    DETAIL_URL: (id: string | number) =>
      `api/v1/notification/system/detail/${id}`,
    EDIT_URL: 'api/v1/notification/system/edit',
  },
  PUSH_NOTIFICATIONS: {
    LIST_URL: 'api/v1/notification/push',
    DETAIL_URL: (id: string | number) =>
      `api/v1/notification/push/detail/${id}`,
    EDIT_URL: 'api/v1/notification/push/edit',
  },
  SYSTEM_SETTINGS: {
    GET_URL: 'api/v1/system-settings',
    UPDATE_URL: 'api/v1/system-settings',
  },
  ACTIVITY_ENTITY: {
    ACTIVITY_ENTITY_LIST_URL: 'api/v1/activity-entity/list',
    ACTIVITY_ENTITY_SINGLE_URL: (id: string | number) =>
      `api/v1/activity-entity/${id}`,
    ACTIVITY_ENTITY_UPDATE_URL: (id: string | number) =>
      `api/v1/activity-entity/${id}`,
    ACTIVITY_ENTITY_CHANGE_STATUS_URL: 'api/v1/activity-entity/change-status',
    ACTIVITY_ENTITY_ADD_URL: 'api/v1/activity-entity/create',
    ACTIVITY_AUTO_COMPLETE_URL: 'api/v1/activity-entity/autocomplete',
  },
  ADMIN: {
    ADMIN_IDENTITY_URL: 'api/v1/identity',
    ADMIN_LIST_URL: 'api/v1/admin/list',
    ADMIN_PROFILE_URL: 'api/v1/admin/profile',
    ADMIN_UPDATE_URL: (id: string | number) => `api/v1/admin/${id}`,
    DELETE_ADMIN_URL: (id: string | number) => `api/v1/admin/${id}`,
    INSERT_ADMIN_URL: 'api/v1/admin',
    DETAILS_ADMIN_URL: (id: string | number) => `api/v1/admin/details/${id}`,
    ADMIN_EXPORT_URL: 'api/v1/admin/export',
    ADMIN_KPI_URL: 'api/v1/admin/kpi',
    FILTER_METADATA_URL: 'api/v1/admin/filter-metadata',
    CHECK_EMAIL_URL: 'api/v1/admin/check-email',
    CHECK_PHONE_NUMBER_URL: 'api/v1/admin/check-phone-number',
    ADMIN_PROFILE_UPDATE_URL: 'api/v1/admin/profile-update',
    ADMIN_SWITCH_ROLE_URL: 'api/v1/admin/switch-role',
  },
  USERS: {
    USER_LIST_URL: 'api/v1/user',
    USER_DELETED_LIST_URL: 'api/v1/user/deleted',
    USER_DETAIL_URL: (userId: string | number) =>
      `api/v1/user/detail/${userId}`,
    USER_CHANGE_STATUS_URL: 'api/v1/user/change-status',
    USER_RESEND_VERIFICATION_URL: 'api/v1/user/resend-verification-email',
  },
  ROLE: {
    ROLE_URL: 'api/v1/user/group-list',
    EDIT_ROLE_URL: 'api/v1/user/group/',
    ADD_ROLE_URL: 'api/v1/user/group',
    PERMISSION_URL: 'api/v1/permission/permission-list',
    GROUP_CAPABILITY_UPDATE_URL: 'api/v1/user/group-capability-update/',
  },
  ACTIVITY_MASTER: {
    ACTIVITY_MASTER_LIST_URL: 'api/v1/activity-master/list',
    ACTIVITY_MASTER_ADD_URL: 'api/v1/activity-master/create',
    ACTIVITY_MASTER_SINGLE_URL: (id: string | number) =>
      `api/v1/activity-master/${id}`,
    ACTIVITY_MASTER_UPDATE_URL: `api/v1/activity-master/`,
  },
  AUTH: {
    CHANGE_PASSWORD_URL: 'api/v1/user/change-password',
  },
  DYNAMIC_MENU: {
    LIST_URL: 'api/v1/menu/adminMenu-list',
    CREATE_URL: 'api/v1/menu/adminMenu',
    UPDATE_URL: `api/v1/menu/adminMenu`,
    DELETE_URL: `api/v1/menu/adminMenu`,
    PARENT_MENU_AUTOCOMPLETE_URL: 'api/v1/menu/parent-menu-autocomplete',
    UPDATE_MENU_ORDER_URL: 'api/v1/menu/adminMenu/update-sequence',
  },
  ADMIN_NOTIFICATIONS: {
    LIST_URL: 'api/v1/notifications/list',
    COUNT_URL: 'api/v1/admin-notifications/count',
    MARK_ALL_READ_URL: 'api/v1/admin-notifications/mark-all-as-read',
  },
  AUTOCOMPLETE: {
    DEPARTMENT: 'api/v1/autocomplete/department',
    SUB_DEPARTMENT: 'api/v1/autocomplete/sub-department',
    DESIGNATION: 'api/v1/autocomplete/designation',
    SKILL: 'api/v1/autocomplete/skill',
    HOD: 'api/v1/autocomplete/hod',
  },
  EVENTS: {
    EVENT_LIST_URL: 'api/v1/events/list',
    EVENT_DETAIL_URL: (id: string | number) => `api/v1/event/detail/${id}`,
    EVENT_PARTICIPANTS_URL: (id: string | number) =>
      `api/v1/event/participants?event_id=${id}`,
  },
  EDIT_PROFILE_URL: 'api/v1/user/edit-profile',
};
